import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { staffActions } from '@/store';
import { staffPositions } from '../lib/trackizy';
import useFeatures from '@/hooks/useFeatures';

const POLL_MS = 10_000;

/**
 * Polls /api/staff/positions/latest every 10s and returns one row per staff
 * member in the org. Rewired in Phase 3 — no longer derives staff rows by
 * filtering the global vehicle-position stream for synthetic
 * `uniqueid LIKE 'staff:%'` devices (Phase 2 retired that pattern).
 *
 * Also mirrors the array into Redux (`state.staff.byUserId`) so the bottom
 * TelemetryPanel can resolve a selected staff person without prop drilling.
 *
 * Returns: { staff: StaffLatestPosition[], loading: boolean }
 *   staff rows are { userId, name, phone, staffRole, lastFix, shift, sos }.
 *   `loading` is true until the FIRST tick settles — without it a caller cannot
 *   tell "no staff in this org" from "the first poll hasn't answered yet", and
 *   the People roster showed its "No staff members yet" empty state on every
 *   cold load for as long as the request took.
 */
export function useStaffPositions({ enabled, refreshKey = 0 }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const { workforce } = useFeatures();

  useEffect(() => {
    // Workforce gates the whole staff-positions surface server-side
    // (orgFlagGuard on /api/staff/positions/*) — skip polling for a
    // non-entitled org, otherwise this fires a 403 every 10s into a
    // permanently-empty People layer with no UI signal. See src/CLAUDE.md's
    // two-level gating rule.
    if (!enabled || !workforce) {
      setData([]);
      setLoading(false);
      dispatch(staffActions.replaceAll([]));
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    async function tick() {
      try {
        const res = await staffPositions.latest();
        if (cancelled) return;
        const next = Array.isArray(res?.staff) ? res.staff : [];
        setData(next);
        dispatch(staffActions.replaceAll(next));
      } catch {
        // silent — polling will retry
      } finally {
        // Settle on failure too: a permanently-failing poll must still let the
        // caller show its empty/error state instead of spinning forever.
        if (!cancelled) setLoading(false);
      }
    }
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // `refreshKey` lets callers force an immediate refetch (e.g. after the
    // PeoplePage drawer creates or removes a staff member) without waiting
    // up to POLL_MS for the next tick.
  }, [enabled, workforce, refreshKey, dispatch]);

  return { staff: data, loading };
}
